import { Component, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { HttpResponse } from '@angular/common/http'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatTableModule } from '@angular/material/table'
import moment from 'moment/moment'
import { finalize } from 'rxjs'
import { ScheduleFleetService } from '../../../core/services/schedule/schedule-fleet/schedule-fleet.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { DriverAutocompleteComponent } from '../../../shared/components/driver-autocomplete/driver-autocomplete.component'
import {
  CronogramaMotoristaVm,
  CronogramaServicoVm,
} from '../../../core/models/schedule-fleet.model'
import {
  downloadBlobFile,
  fileNameFromContentDisposition,
  parseBlobError,
} from '../../../shared/utils/download_base64'
import { rotuloStatusEscala } from '../../../shared/utils/utils'

// O dia de um motorista: o que ele atende, na ordem do relógio. Uma consulta só
// alimenta os três formatos — a tela mostra o JSON, e Excel/PDF baixam o mesmo dia.
@Component({
  selector: 'cronograma-motorista',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTableModule,
    DriverAutocompleteComponent,
  ],
  templateUrl: './cronograma-motorista.component.html',
  styleUrl: './cronograma-motorista.component.scss',
  standalone: true,
})
export class CronogramaMotoristaComponent implements OnInit {
  constructor(
    private scheduleFleetService: ScheduleFleetService,
    private snackbar: SnackbarCustomService,
  ) {}

  displayedColumns = [
    'servico',
    'horario',
    'tipoServico',
    'percurso',
    'passageiros',
    'veiculo',
    'fornecedor',
    'status',
  ]

  motorista: any = null
  data: Date | null = null

  loading = false
  baixandoExcel = false
  baixandoPdf = false
  buscou = false
  cronograma: CronogramaMotoristaVm | null = null
  // Só a tela: Excel e PDF são montados pelo back e continuam "Agendar"/"Agendado".
  rotuloStatusEscala = rotuloStatusEscala

  ngOnInit(): void {
    this.data = new Date()
  }

  get podeConsultar(): boolean {
    return !!this.motorista && !!this.data
  }

  onMotoristaChange(motorista: any | null): void {
    this.motorista = motorista
  }

  consultar(): void {
    if (!this.podeConsultar) return
    this.loading = true
    this.buscou = true
    this.scheduleFleetService
      .getCronogramaMotorista(this.motorista.id, this.dataParam())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.cronograma = res.data ?? null
        },
        error: (err: any) => {
          this.cronograma = null
          this.snackbar.errorRequest(err)
        },
      })
  }

  baixarExcel(): void {
    if (!this.podeConsultar || this.baixandoExcel) return
    this.baixandoExcel = true
    this.scheduleFleetService
      .exportarCronogramaMotoristaExcel(this.motorista.id, this.dataParam())
      .pipe(finalize(() => (this.baixandoExcel = false)))
      .subscribe({
        next: (res) => this.salvarArquivo(res, 'xlsx'),
        error: async (err: any) => this.snackbar.errorRequest(await parseBlobError(err)),
      })
  }

  // Um clique, um PDF: cada geração sobe um Chromium no servidor e entra numa fila de
  // um por vez, então o botão fica travado até a resposta voltar.
  baixarPdf(): void {
    if (!this.podeConsultar || this.baixandoPdf) return
    this.baixandoPdf = true
    this.scheduleFleetService
      .exportarCronogramaMotoristaPdf(this.motorista.id, this.dataParam())
      .pipe(finalize(() => (this.baixandoPdf = false)))
      .subscribe({
        next: (res) => this.salvarArquivo(res, 'pdf'),
        error: async (err: any) => this.snackbar.errorRequest(await parseBlobError(err)),
      })
  }

  // Serviço com vários passageiros traz todos separados por "/", e os contatos vêm
  // na mesma ordem dos nomes — mostrar só o primeiro esconderia trabalho do motorista.
  passageiros(servico: CronogramaServicoVm): { nome: string; contato: string }[] {
    const nomes = (servico.passageiro ?? '').split('/').map((n) => n.trim()).filter(Boolean)
    const contatos = (servico.contato ?? '').split('/').map((c) => c.trim())
    return nomes.map((nome, i) => ({ nome, contato: contatos[i] ?? '' }))
  }

  // Cancelada continua na folha, riscada — quem recebeu a versão anterior precisa
  // entender que perdeu a corrida, não que a folha veio errada.
  cancelado(servico: CronogramaServicoVm): boolean {
    return (servico.descricaoStatus ?? '').toLowerCase().includes('cancel')
  }

  private dataParam(): string {
    return moment(this.data).format('DD/MM/YYYY')
  }

  private salvarArquivo(res: HttpResponse<Blob>, extensao: 'xlsx' | 'pdf'): void {
    if (!res.body) return
    const nomeMotorista = (this.motorista?.nome ?? 'MOTORISTA')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/\s+/g, '_')
    const fallback = `OS_${nomeMotorista}_${moment(this.data).format('DD-MM-YYYY')}.${extensao}`
    downloadBlobFile(
      res.body,
      fileNameFromContentDisposition(res.headers.get('Content-Disposition'), fallback),
    )
  }
}
